import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { forkJoin, finalize } from 'rxjs';
import { PermissionDefinition, Role } from '../../core/models/user.models';
import { PermissionsService } from '../../core/services/permissions.service';
import { RolesService } from '../../core/services/roles.service';
import { httpErrorMessage } from '../../core/utils/http-error-message';
import { AuthService } from '../../core/auth/auth.service';
import { ActivatedRoute } from '@angular/router';
import { CurrentUserService } from '../../core/auth/current-user.service';

@Component({
  selector: 'app-permissions',
  imports: [FormsModule],
  templateUrl: './permissions.html',
  styleUrl: './permissions.scss',
})
export class Permissions implements OnInit {
  private readonly permissionsService = inject(PermissionsService);
  private readonly rolesService = inject(RolesService);
  private readonly auth = inject(AuthService);
  private readonly route = inject(ActivatedRoute);
  private readonly currentUser = inject(CurrentUserService);
  private readonly changeDetector = inject(ChangeDetectorRef);
  roles: Role[] = [];
  permissions: PermissionDefinition[] = [];
  selectedRoleId = '';
  selectedPermissionIds = new Set<string>();
  isLoading = true;
  isRoleLoading = false;
  isSaving = false;
  errorMessage = '';
  successMessage = '';
  isPlatformAdmin = false;
  get canAssignPermissions(): boolean { return this.currentUser.hasPermission('PERMISSION_ASSIGN'); }

  get selectedRole(): Role | undefined {
    return this.roles.find((role) => role.id === this.selectedRoleId);
  }
  get modules(): string[] {
    return [...new Set(this.permissions.map((permission) => permission.module || 'Other'))].sort();
  }

  ngOnInit(): void {
    const roleNames = this.currentUser.user()?.roles ?? [this.currentUser.user()?.role ?? ''];
    this.isPlatformAdmin = roleNames.some((role) => role === 'SuperAdmin' || role === 'PlatformSuperAdmin');
    if (this.isPlatformAdmin) {
      this.loadPage();
      return;
    }
    this.loadPage();
  }

  loadPage(): void {
    this.isLoading = true; this.errorMessage = ''; this.roles = []; this.selectedRoleId = ''; this.selectedPermissionIds.clear();
    forkJoin({ roles: this.rolesService.list(), permissions: this.permissionsService.list() })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: ({ roles, permissions }) => {
          this.roles = roles;
          this.permissions = permissions;
          if (roles.length) {
            const requestedRoleId = this.route.snapshot.queryParamMap.get('roleId');
            this.selectedRoleId = roles.some((role) => role.id === requestedRoleId) ? requestedRoleId! : roles[0].id;
            this.loadRolePermissions();
          }
          this.isLoading = false;
          this.renderNow();
        },
        error: (error: unknown) => {
          this.isLoading = false;
          this.errorMessage = httpErrorMessage(error, 'Unable to load roles and permissions.');
          this.renderNow();
        },
      });
  }

  permissionsFor(module: string): PermissionDefinition[] {
    return this.permissions.filter((permission) => (permission.module || 'Other') === module);
  }

  moduleSelectedCount(module: string): number {
    return this.permissionsFor(module).filter((permission) => this.selectedPermissionIds.has(permission.id)).length;
  }

  isModuleFullySelected(module: string): boolean {
    const permissions = this.permissionsFor(module);
    return permissions.length > 0 && permissions.every((permission) => this.selectedPermissionIds.has(permission.id));
  }

  toggleModule(module: string): void {
    if (!this.canAssignPermissions || this.isRoleLoading) return;
    const modulePermissions = this.permissionsFor(module);
    const shouldSelect = !this.isModuleFullySelected(module);
    const next = new Set(this.selectedPermissionIds);
    for (const permission of modulePermissions) {
      shouldSelect ? next.add(permission.id) : next.delete(permission.id);
    }
    this.selectedPermissionIds = next;
  }

  loadRolePermissions(): void {
    if (!this.selectedRoleId) return;
    this.isRoleLoading = true;
    this.errorMessage = '';
    this.successMessage = '';
    this.rolesService
      .permissionIds(this.selectedRoleId)
      .pipe(finalize(() => (this.isRoleLoading = false)))
      .subscribe({
        next: (ids) => {
          this.selectedPermissionIds = new Set(ids);
          this.isRoleLoading = false;
          this.renderNow();
        },
        error: (error: unknown) => {
          this.errorMessage = httpErrorMessage(error, 'Unable to load role permissions.');
          this.isRoleLoading = false;
          this.renderNow();
        },
      });
  }

  toggle(permissionId: string, enabled: boolean): void {
    if (!this.canAssignPermissions || this.isRoleLoading) return;
    const next = new Set(this.selectedPermissionIds);
    enabled ? next.add(permissionId) : next.delete(permissionId);
    this.selectedPermissionIds = next;
  }

  save(): void {
    if (!this.selectedRoleId) return;
    this.isSaving = true;
    this.errorMessage = '';
    this.successMessage = '';
    this.rolesService
      .replacePermissions(this.selectedRoleId, [...this.selectedPermissionIds])
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe({
        next: (ids) => {
          this.selectedPermissionIds = new Set(ids);
          this.auth.refreshSession().subscribe({ error: () => undefined });
          this.successMessage = `Permissions saved for ${this.selectedRole?.name ?? 'role'}. Access is updated automatically.`;
          this.isSaving = false;
          this.renderNow();
        },
        error: (error: unknown) => {
          this.errorMessage = httpErrorMessage(error, 'Unable to save role permissions.');
          this.isSaving = false;
          this.renderNow();
        },
      });
  }

  private renderNow(): void {
    this.changeDetector.detectChanges();
  }
}
